import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/**
 * MyCasesPage — 个人中心「我的案例」（/u/:username/cases）
 *
 * 语义：列出**当前登录用户自己发起**的案例贴（不含被分配的导师案例）。
 *
 * - 路由：user.taroCases → /u/:username/cases（与 user.taroCredits 风格一致）
 * - 数据：GET /api/taro/cases（CaseListController 返回当前用户可见案例，
 *   含自己的 private；前端过滤 user_id === 路由用户 即"我发起的"）
 * - 越权：URL 里的 username 不是当前用户（且非管理员）→ 拦截提示
 * - 后端 route /u/{username}/cases 与 /my-cases（别名）均已注册；
 *   与 credits 一样直接刷新可能落到 404（Flarum core /u/{username}[/{filter}] 路由
 *   优先匹配），SPA 导航不受影响。
 */
const STATUS_MAP = {
  draft: { label: '草稿', cls: 'is-draft' },
  pending: { label: '处理中', cls: 'is-pending' },
  processing: { label: '处理中', cls: 'is-pending' },
  published: { label: '已发布', cls: 'is-published' },
  completed: { label: '已完成', cls: 'is-completed' },
  archived: { label: '已归档', cls: 'is-draft' },
};

export default class MyCasesPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.loading = true;
    this.cases = [];
    this.error = null;
    this.forbidden = false;

    // 路由参数：/u/:username/cases
    this.username = m.route.param('username');
    this.user = app.session.user;
    if (!this.user) return; // guest 交给强制登录逻辑

    // 越权检查：URL 是别人的用户名且当前用户非管理员 → 拦截
    const routeSlug = decodeURIComponent(this.username || '');
    const mySlug = this.user.slug();
    const isAdmin = typeof this.user.isAdmin === 'function' && this.user.isAdmin();
    if (routeSlug && routeSlug !== mySlug && !isAdmin) {
      this.forbidden = true;
      this.loading = false;
      return;
    }

    this.loadCases();
  }

  loadCases() {
    this.loading = true;
    this.error = null;

    app
      .request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/taro/cases',
        params: { limit: 50 },
      })
      .then((res) => {
        const all = (res && res.data) || [];
        // 语义明确：只显示"我发起的"（CaseListController 已保证 private 只有自己的，
        // 这里再按 user_id 过滤一遍保证"我发起的案例"语义）
        this.cases = all.filter((c) => {
          const uid = c.attributes && (c.attributes.user_id || c.attributes.userId);
          return String(uid) === String(this.user.id());
        });
        this.loading = false;
        m.redraw();
      })
      .catch((err) => {
        this.loading = false;
        this.error =
          (err && err.response && err.response.errors && err.response.errors[0] && err.response.errors[0].detail) ||
          '加载案例失败';
        m.redraw();
      });
  }

  title() {
    return '我的案例';
  }

  view() {
    return (
      <div className="MyCasesPage">
        <div className="container">
          <h2 className="MyCasesPage-title">
            <i className="fas fa-folder-open" /> 我的案例
          </h2>

          {this.forbidden ? (
            <div className="Alert Alert--error">
              <i className="fas fa-ban" /> 无权查看他人案例
            </div>
          ) : this.loading ? (
            <LoadingIndicator />
          ) : this.error ? (
            <div className="Alert Alert--error">{this.error}</div>
          ) : this.cases.length === 0 ? (
            <div className="MyCasesPage-empty">
              <i className="fas fa-inbox" />
              <p>你还没有发布过案例</p>
              <p className="MyCasesPage-hint">在左侧「案例板块」选择你的等级板块，或通过右栏萨拉的「发布案例」按钮发起第一个案例。</p>
            </div>
          ) : (
            <ul className="MyCasesPage-list">
              {this.cases.map((c) => {
                const a = c.attributes || {};
                const status = STATUS_MAP[a.status] || { label: a.status || '未知', cls: '' };
                const discussionId = a.discussion_id || a.discussionId;
                const title =
                  (a.case_data && (a.case_data.title || a.case_data.question)) ||
                  (discussionId ? '案例 #' + discussionId : '未命名案例');
                const createdAt = a.createdAt || a.created_at;
                return (
                  <li className="MyCasesPage-item" key={c.id}>
                    <div className="MyCasesPage-main">
                      <a
                        className="MyCasesPage-titleLink"
                        href={discussionId ? '/d/' + discussionId : '#'}
                        onclick={(e) => {
                          if (!discussionId) { e.preventDefault(); return; }
                          e.preventDefault();
                          m.route.set('/d/' + discussionId);
                        }}
                      >
                        {String(title).slice(0, 60)}
                      </a>
                      <span className={'MyCasesPage-status ' + status.cls}>{status.label}</span>
                    </div>
                    <div className="MyCasesPage-meta">
                      {createdAt ? (
                        <span className="MyCasesPage-time">
                          <i className="far fa-clock" /> {new Date(createdAt).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}
                        </span>
                      ) : null}
                      <span className="MyCasesPage-level">
                        {a.required_level || a.requiredLevel || '-'}
                      </span>
                      {a.visibility === 'private' ? (
                        <span className="MyCasesPage-private">
                          <i className="fas fa-lock" /> 私密
                        </span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    );
  }
}